import React, { useState } from 'react';
import { FileText, Save } from 'lucide-react';
import type { CmsDocument } from '../types';
import { loadCms, saveCms } from '../lib/skyluxStore';

const empty: CmsDocument = {
  landingHeroTitle: '',
  landingHeroSubtitle: '',
  quoteEmailIntro: '',
  paymentReminderBody: '',
};

const AdminCMS = () => {
  const [doc, setDoc] = useState<CmsDocument>(() => ({ ...empty, ...loadCms() }));
  const [saved, setSaved] = useState(false);

  const persist = () => {
    saveCms(doc);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  const field = (key: keyof CmsDocument, label: string, rows = 2) => (
    <div key={key}>
      <label style={{ fontSize: '0.75rem', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: '0.5rem' }}>{label}</label>
      {rows > 1 ? (
        <textarea rows={rows} value={doc[key]} onChange={(e) => setDoc((d) => ({ ...d, [key]: e.target.value }))} style={{ minHeight: rows === 1 ? undefined : `${rows * 1.25}rem` }} />
      ) : (
        <input value={doc[key]} onChange={(e) => setDoc((d) => ({ ...d, [key]: e.target.value }))} />
      )}
    </div>
  );

  return (
    <div className="animate-fade">
      {saved && (
        <div style={{ position: 'fixed', top: '100px', right: '40px', background: 'var(--success)', color: 'white', padding: '1rem 1.5rem', borderRadius: '0.75rem', boxShadow: 'var(--shadow-lg)', zIndex: 100 }}>
          Content saved
        </div>
      )}
      <div style={{ marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '2.25rem', fontWeight: '800', letterSpacing: '-0.025em' }}>Content management</h2>
        <p style={{ color: 'var(--text-muted)', maxWidth: '720px' }}>
          Edit marketing copy and email snippets used across the demo. In production this would sync to your CMS API or
          headless source. Quote emails merge the intro below with flight and link data from the agent workflow.
        </p>
      </div>

      <div style={{ background: 'white', borderRadius: '1.5rem', border: '1px solid var(--border)', padding: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
          <div style={{ padding: '0.6rem', borderRadius: '12px', background: '#eff6ff' }}>
            <FileText size={22} color="var(--accent)" />
          </div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '800' }}>Site & email strings</h3>
        </div>

        <div style={{ display: 'grid', gap: '1.5rem', maxWidth: '800px' }}>
          {field('landingHeroTitle', 'Landing — hero title', 1)}
          {field('landingHeroSubtitle', 'Landing — hero subtitle', 2)}
          {field('quoteEmailIntro', 'Quote email — extra opening line (optional; intro is auto-generated from client, agent, and route)', 3)}
          {field('paymentReminderBody', 'Payment reminder — body', 3)}
        </div>

        <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={persist}
            style={{
              background: 'var(--primary)',
              color: 'white',
              padding: '0.75rem 1.75rem',
              borderRadius: '0.75rem',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Save size={18} /> Save CMS
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCMS;
